import React, { useState, useEffect, useReducer, useContext, useRef } from "react";

import { makeStyles } from 'tss-react/mui';
import List from "@mui/material/List";
import Paper from "@mui/material/Paper";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { socketConnection } from "../../services/socket";

const useStyles = makeStyles()((theme) => ({
  ticketsListWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 0,
  },

  ticketsList: {
    flex: 1,
    maxHeight: "100%",
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    borderTop: "2px solid rgba(0, 0, 0, 0.12)",
  },

  ticketsListHeader: {
    color: "rgb(67, 83, 105)",
    zIndex: 2,
    backgroundColor: "white",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  ticketsCount: {
    fontWeight: "normal",
    color: "rgb(104, 121, 146)",
    marginLeft: "8px",
    fontSize: "14px",
  },

  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },

  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },

  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const reducer = (state, action) => {

  if (action.type === "LOAD_TICKETS") {
    const newTickets = action.payload;
    newTickets.forEach((ticket) => {
      const ticketIndex = state.findIndex((t) => t.id === ticket.id);
      if (ticketIndex !== -1) {
        state[ticketIndex] = ticket;
        if (ticket.unreadMessages > 0) {
          state.unshift(state.splice(ticketIndex, 1)[0]);
        }
      } else {
        state.push(ticket);
      }
    });

    return [...state];
  }

  if (action.type === "RESET_UNREAD") {
    const ticketId = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex].unreadMessages = 0;
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket;
    } else {
      state.unshift(ticket);
    }
    return [...state];
  }

  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket;
      state.unshift(state.splice(ticketIndex, 1)[0]);
    } else {
      state.unshift(ticket);
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;
    const ticketIndex = state.findIndex((t) => t.contactId === contact.id);
    if (ticketIndex !== -1) {
      state[ticketIndex].contact = contact;
    }
    return [...state];
  }

  if (action.type === "DELETE_TICKET") {
    const ticketId = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state.splice(ticketIndex, 1);
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const TicketsListCustom = (props) => {
  const {
    status,
    searchParam,
    searchMessage,
    tags,
    users,
    usersLast,
    whatsappIdLast,
    showAll,
    whatsappId,
    selectedQueueIds,
    statusId,
    updateCount,
    updateTotal,
    style,
    withUnreadMessages,
    archived,
    orderByRecent,
    adReferralSourceId
  } = props;
  const { classes } = useStyles();
  const [pageNumber, setPageNumber] = useState(1);
  const [ticketsList, dispatch] = useReducer(reducer, []);
  const { user } = useContext(AuthContext);
  const { profile, queues } = user;

  const [liveTotal, setLiveTotal] = useState(0);
  const idsRef = useRef(new Set());
  const bumpTotal = (delta) =>
    setLiveTotal((t) => Math.max(0, t + delta));

  useEffect(() => {
    idsRef.current = new Set(ticketsList.map((t) => t.id));
  }, [ticketsList]);

  const [cursor, setCursor] = useState(null);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setCursor(null);

  }, [status, orderByRecent, searchParam, searchMessage, dispatch, showAll, withUnreadMessages, archived, tags, statusId, users, usersLast, whatsappId, selectedQueueIds, whatsappIdLast, adReferralSourceId]);

  const { tickets, hasMore, loading, total } = useTickets({
    pageNumber,
    searchParam,
    searchMessage,
    status,
    showAll,
    whatsappId,
    whatsappIdLast,
    tags: JSON.stringify(tags),
    users: JSON.stringify(users),
    usersLast: JSON.stringify(usersLast),
    queueIds: JSON.stringify(selectedQueueIds),
    statusId,
    withUnreadMessages,
    archived,
    orderByRecent,
    adReferralSourceId,
    beforeUpdatedAt: cursor?.beforeUpdatedAt,
    beforeId: cursor?.beforeId
  });

  useEffect(() => {
    if (!loading) setLiveTotal(total);
  }, [total, loading]);

  useEffect(() => {
    if (loading) return;

    const queueIds = queues.map((q) => q.id);
    const filteredTickets = tickets.filter((t) => {
      return (
        queueIds.indexOf(t.queueId) > -1 ||
        t.queueId === null ||
        t.userId === user?.id
      );
    });

    if (profile === "admin") {
      dispatch({ type: "LOAD_TICKETS", payload: tickets });
    } else {
      dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
    }

  }, [tickets, queues, profile, loading]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    const isAdmin = profile === "admin" || profile === "supervisor";

    const queueMatches = (ticket) => {
      if (status === "closed") {
        return (
          selectedQueueIds.indexOf(ticket.queueidlast) > -1 ||
          (isAdmin && !ticket.queueidlast)
        );
      }
      if (status === "pending" || status === "bot") {
        return (
          selectedQueueIds.indexOf(ticket.queueId) > -1 ||
          (isAdmin && !ticket.queueId)
        );
      }
      if (status === "open") {
        return (
          selectedQueueIds.indexOf(ticket.queueId) > -1 ||
          (isAdmin && showAll && !ticket.queueId)
        );
      }
      return true;
    };

    const matchesActiveFilters = (ticket) => {
      if (!ticket) return false;

      if (status === "ads") {
        if (!ticket.adReferralSourceId) return false;
        if (!["open", "pending"].includes(ticket.status)) return false;
      } else if (status && ticket.status !== status) {
        return false;
      }

      if (whatsappId && String(ticket.whatsappId) !== String(whatsappId)) {
        return false;
      }
      if (
        whatsappIdLast &&
        String(ticket.whatsappidlast) !== String(whatsappIdLast)
      ) {
        return false;
      }

      if (statusId && String(ticket.statusId) !== String(statusId)) return false;

      if (
        adReferralSourceId &&
        String(ticket.adReferralSourceId) !== String(adReferralSourceId)
      ) {
        return false;
      }

      if (Array.isArray(tags) && tags.length > 0) {
        const ticketTagIds = (ticket.tags || []).map((t) => t.id);
        if (!tags.some((tagId) => ticketTagIds.includes(tagId))) return false;
      }

      if (Array.isArray(users) && users.length > 0) {
        if (!users.includes(ticket.userId)) return false;
      }
      if (Array.isArray(usersLast) && usersLast.length > 0) {
        if (!usersLast.includes(ticket.useridlast)) return false;
      }

      if (withUnreadMessages === "true" && !ticket.unreadMessages) return false;

      const esArchivado = !!ticket.archived;
      if ((archived === "true") !== esArchivado) return false;

      return true;
    };

    const shouldUpdateTicket = (ticket) =>
      (!ticket.userId || ticket.userId === user?.id || showAll) &&
      (ticket.userId === user?.id || queueMatches(ticket)) &&
      matchesActiveFilters(ticket);

    const notBelongsToUserQueues = (ticket) =>
      ticket.userId !== user?.id && !queueMatches(ticket);

    const notBelongsToUser = (ticket) =>
      !showAll &&
      ticket.userId &&
      ticket.userId !== user?.id &&
      !["pending", "bot", "favorite"].includes(ticket.status);

    socket.on("connect", () => {
      if (status) {
        socket.emit("joinTickets", status);
      } else {
        socket.emit("joinNotification");
      }
    });

    socket.on(`company-${companyId}-ticket`, (data) => {

      if (status === "open" && !showAll && data.ticket) {
        const t = data.ticket;
        console.log(
          `[MIOS] evt=${data.action} ticket=${t.id} status=${t.status} queueId=${t.queueId} ` +
          `ticket.userId=${t.userId}(${typeof t.userId}) me=${user?.id}(${typeof user?.id}) ` +
          `| esMio=${t.userId === user?.id} shouldUpdate=${shouldUpdateTicket(t)} ` +
          `delQueues=${notBelongsToUserQueues(t)} delUser=${notBelongsToUser(t)} ` +
          `enLista=${idsRef.current.has(t.id)}`
        );
      }

      if (data.action === "updateUnread") {
        dispatch({
          type: "RESET_UNREAD",
          payload: data.ticketId,
        });
      }

      if (data.action === "update" && shouldUpdateTicket(data.ticket)) {
        if (!idsRef.current.has(data.ticket.id)) bumpTotal(1);
        dispatch({
          type: "UPDATE_TICKET",
          payload: data.ticket,
        });
      }

      if (
        data.action === "update" &&
        (notBelongsToUserQueues(data.ticket) ||
          notBelongsToUser(data.ticket) ||
          !matchesActiveFilters(data.ticket))
      ) {
        if (idsRef.current.has(data.ticket.id)) bumpTotal(-1);
        dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
      }

      if (data.action === "delete") {
        if (idsRef.current.has(data.ticketId)) bumpTotal(-1);
        dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
      }
    });

    socket.on(`company-${companyId}-appMessage`, (data) => {
      if (data.action === "create" && shouldUpdateTicket(data.ticket)) {
        if (!idsRef.current.has(data.ticket.id)) bumpTotal(1);
        dispatch({
          type: "UPDATE_TICKET_UNREAD_MESSAGES",
          payload: data.ticket,
        });
      }
    });

    socket.on(`company-${companyId}-contact`, (data) => {
      if (data.action === "update") {
        dispatch({
          type: "UPDATE_TICKET_CONTACT",
          payload: data.contact,
        });
      }
    });

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    status,
    showAll,
    user?.id,
    JSON.stringify(selectedQueueIds),
    whatsappId,
    JSON.stringify(tags),
    JSON.stringify(usersLast),
    JSON.stringify(users),
    profile,
    JSON.stringify(queues),
    whatsappIdLast,
    statusId,
    adReferralSourceId,
    withUnreadMessages,
    archived,
  ]);

  useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(liveTotal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveTotal]);

  const loadMore = () => {
    if (ticketsList.length === 0) return;

    const boundary = ticketsList.reduce((acc, t) => {
      if (!acc) return t;
      const a = new Date(acc.updatedAt).getTime();
      const b = new Date(t.updatedAt).getTime();
      if (orderByRecent) {
        if (b < a || (b === a && t.id < acc.id)) return t;
      } else {
        if (b > a || (b === a && t.id > acc.id)) return t;
      }
      return acc;
    }, null);

    if (!boundary) return;

    setCursor({ beforeUpdatedAt: boundary.updatedAt, beforeId: boundary.id });
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <Paper className={classes.ticketsListWrapper} style={style}>
      <Paper
        square
        name="closed"
        elevation={0}
        className={classes.ticketsList}
        onScroll={handleScroll}
      >
        <List style={{ paddingTop: 0 }}>
          {ticketsList.length === 0 && !loading ? (
            <div className={classes.noTicketsDiv}>
              <span className={classes.noTicketsTitle}>
                {i18n.t("ticketsList.noTicketsTitle")}
              </span>
              <p className={classes.noTicketsText}>
                {i18n.t("ticketsList.noTicketsMessage")}
              </p>
            </div>
          ) : (
            <>
              {ticketsList.map((ticket) => (
                <TicketListItem ticket={ticket} key={ticket.id} />
              ))}
            </>
          )}
          {loading && <TicketsListSkeleton />}
        </List>
      </Paper>
    </Paper>
  );
};

export default TicketsListCustom;